import type {
  EnvironmentId,
  ProviderKind,
  ScopedThreadRef,
  ServerProvider,
} from "@cmmd-forge/contracts";
import type { UnifiedSettings } from "@cmmd-forge/contracts/settings";
import { useMemo } from "react";

import {
  useForgeProviderAccess,
  type ProviderCredentialMode,
} from "../../cmmd/forgeProviderAccess";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import type { DraftId } from "../../draftId";
import type { Project, Thread } from "../../types";
import { resolveComposerTurnPlan } from "./composerTurnPlan";
import {
  useComposerModelSelection,
  type ComposerModelSelection,
} from "./useComposerModelSelection";

export function useChatComposerProviderConfiguration(input: {
  readonly environmentId: EnvironmentId;
  readonly providerStatuses: ReadonlyArray<ServerProvider>;
  readonly composerActiveProvider: ProviderKind | null;
  readonly composerDraftTarget: ScopedThreadRef | DraftId;
  readonly activeThread: Thread | undefined;
  readonly activeProject: Project | null | undefined;
  readonly settings: UnifiedSettings;
}) {
  const environmentClassification = classifyEnvironmentId(input.environmentId);
  const providerCredentialMode: ProviderCredentialMode = environmentClassification.isCloud
    ? "cmmd-cloud"
    : "runtime";
  // The environment id goes to the access seam unresolved: the seam resolves
  // the readiness target from it, so a durable `env_` Environment asks CMMD by
  // its own id instead of collapsing to a null runtime id that never polls
  // (FOR-3384). Credential mode still comes from the classification above.
  const providerAccess = useForgeProviderAccess({
    enabled: true,
    credentialMode: providerCredentialMode,
    environmentId: input.environmentId,
  });
  // One pure decision for the whole composer: selection, picker rows, the Run
  // verdict that gates the send, and the snapshots model resolution may draw
  // from. The send path used to recompute its own answer from the eligibility
  // subset here and could disagree with the picker (forge#3385); both now read
  // the same plan.
  const plan = useMemo(
    () =>
      resolveComposerTurnPlan({
        thread: input.activeThread,
        selectedProvider: input.composerActiveProvider,
        projectDefaultProvider: input.activeProject?.defaultModelSelection?.provider ?? null,
        providers: input.providerStatuses,
        access: providerAccess,
        credentialMode: providerCredentialMode,
        hasLiveInventory: true,
      }),
    [
      input.activeThread,
      input.composerActiveProvider,
      input.activeProject,
      input.providerStatuses,
      providerAccess,
      providerCredentialMode,
    ],
  );
  const activeProviderStatus = useMemo(
    () => input.providerStatuses.find((status) => status.provider === plan.selection) ?? null,
    [input.providerStatuses, plan.selection],
  );
  const composerModelSelection = useSubmissionComposerModelSelection({
    composerDraftTarget: input.composerDraftTarget,
    providerStatuses: input.providerStatuses,
    runnableProviders: plan.runnableProviders,
    selectedProvider: plan.selection,
    lockedProvider: plan.lockedProvider,
    activeThread: input.activeThread,
    activeProject: input.activeProject,
    settings: input.settings,
  });

  return {
    selectedProvider: plan.selection,
    lockedProvider: plan.lockedProvider,
    threadProvider: plan.threadProvider,
    composerDisabledReason: plan.refusalReason,
    providerAccess,
    providerCredentialMode,
    providerAvailability: plan.pickRows,
    activeProviderStatus,
    composerModelSelection,
  };
}

function useSubmissionComposerModelSelection(input: {
  readonly composerDraftTarget: ScopedThreadRef | DraftId;
  readonly providerStatuses: ReadonlyArray<ServerProvider>;
  readonly runnableProviders: ReadonlyArray<ServerProvider>;
  readonly selectedProvider: ProviderKind;
  readonly lockedProvider: ProviderKind | null;
  readonly activeThread: Thread | undefined;
  readonly activeProject: Project | null | undefined;
  readonly settings: UnifiedSettings;
}): ComposerModelSelection {
  // An unlocked send resolves models over the runnable snapshots (Run verdict
  // usable); a locked thread keeps the full list, its provider already fixed.
  const composerProviderStatuses = useMemo(
    () =>
      input.lockedProvider === null ? [...input.runnableProviders] : [...input.providerStatuses],
    [input.providerStatuses, input.runnableProviders, input.lockedProvider],
  );
  return useComposerModelSelection({
    threadRef: input.composerDraftTarget,
    providers: composerProviderStatuses,
    resolvedProvider: input.selectedProvider,
    lockedProvider: input.lockedProvider,
    threadModelSelection: input.activeThread?.modelSelection,
    projectModelSelection: input.activeProject?.defaultModelSelection,
    settings: input.settings,
  });
}
